import React from "react";
import {
    Book,
    Users,
    HelpCircle,
    MessageCircle,
    Package,
    FileCodeIcon as FileContract,
    FileText,
    Wrench,
    AlertTriangle
} from "lucide-react";

export const categories = [
    { id: "faq", name: "FAQ", icon: <HelpCircle size={16} /> },
    {
        id: "contact",
        name: "Contact Support",
        icon: <MessageCircle size={16} />
    },
    {
        id: "fault-reporting",
        name: "Fault Reporting",
        icon: <AlertTriangle size={16} />
    },
    {
        id: "service-request",
        name: "Service Request",
        icon: <FileText size={16} />
    },
    {
        id: "work-order",
        name: "Work Order",
        icon: <Wrench size={16} />
    },
    {
        id: "asset-management",
        name: "Asset Management",
        icon: <Package size={16} />
    },
    {
        id: "sla-management",
        name: "SLA Management",
        icon: <FileContract size={16} />
    },
    { id: "administration", name: "Administration", icon: <Users size={16} /> },
    {
        id: "pricebook-warehouse",
        name: "Pricebook & Warehouse",
        icon: <Package size={16} />
    }
];

export const faqs = [
    // Basic Operations & Account Management
    {
        question: "How do I reset my password?",
        answer:
            "Account > Change Password or use the 'Forgot Password' link on the login page.",
        category: "Account Management"
    },
    {
        question: "How do I download the ANIKE mobile app?",
        answer:
            "ANIKE is available for iPhone and Android. On iPhone, install it from the App Store: https://apps.apple.com/app/id1578342634 On Android, install it from Google Play: https://play.google.com/store/apps/details?id=com.apexapps.anikev2 Then sign in with the same email and password you use on the website.",
        category: "Account Management"
    },
    {
        question: "What are the default user roles in ANIKE?",
        answer:
            "ANIKE has 5 default roles: Asset Owner (manages assets and strategic decisions), MRO (handles maintenance coordination), Technician (performs hands-on work), Super Admin (full system access), and Supplier (manages vendor relationships). Each can create custom roles within their organization.",
        category: "User Management"
    },
    {
        question: "How do I create a new organization account?",
        answer:
            "Visit the registration portal at https://www.anike.ng/auth/registration to submit a request for a new organization account. Once approved, you can start creating custom roles and adding users.",
        category: "Account Management"
    },

    // Asset Management FAQs
    {
        question: "How do I add a new asset?",
        answer:
            "Click the 'Create' button and select 'Asset' from the dropdown. Fill in the asset details including Asset Name, Asset ID, Location ID, Location Name, Asset Description, Asset Condition, and Manufacturer/Supplier information.",
        category: "Asset Management"
    },
    {
        question: "Can I upload multiple assets at once?",
        answer:
            "Yes, use the 'Bulk upload Asset' feature from the Create menu. Download the Excel template, fill it with your asset data ensuring all required fields are completed, and upload the file.",
        category: "Asset Management"
    },
    {
        question: "What is a cluster and how do I create one?",
        answer:
            "A cluster is a group of related assets managed together. Create one by selecting 'Cluster' from the Create menu, entering the Cluster Number and Condition, selecting the State, choosing assets to add, and assigning a Responsible Service Provider.",
        category: "Asset Management"
    },
    {
        question: "How do I assign assets to a cluster?",
        answer:
            "Navigate to the unassigned assets list, use search functionality to find specific assets, select the assets you want to group, and either create a new cluster or add them to an existing cluster through the Actions menu.",
        category: "Asset Management"
    },
    {
        question:
            "What's the difference between Asset Owner, MRO, and Technician asset views?",
        answer:
            "Asset Owners see all assets and can create/manage clusters. MROs see 'Assigned Assets' (delegated to them) and 'Organization Assets' (owned by them). Technicians only see assets specifically assigned to them by their MRO organization.",
        category: "Asset Management"
    },

    // SLA Management FAQs
    {
        question: "What is an SLA and how do I create one?",
        answer:
            "A Service Level Agreement (SLA) defines performance standards and response times for service providers. Create one by going to 'SLA Management' > 'Create SLA', entering a descriptive title, defining detailed terms with measurable criteria, and uploading supporting documents.",
        category: "SLA Management"
    },
    {
        question: "How do SLAs bind service providers to work orders?",
        answer:
            "When Asset Owners create work orders and assign them to service providers, the applicable SLA automatically binds the service provider to meet the defined performance standards, response times, and quality metrics.",
        category: "SLA Management"
    },
    {
        question: "Can I attach documents to an SLA?",
        answer:
            "Yes, click 'Choose file' when creating an SLA to upload supporting documents such as detailed contracts, technical specifications, or compliance requirements that supplement the SLA terms.",
        category: "SLA Management"
    },

    // Service Request FAQs
    {
        question:
            "What's the difference between a service request and an observation?",
        answer:
            "Service requests are operations to be performed on assets (work that needs to be done). Observations are reports of fault findings and repair requirements submitted by MROs after performing those operations.",
        category: "Service Requests"
    },
    {
        question: "How do I create a service request as an Asset Owner?",
        answer:
            "Navigate to 'Service Request' > 'Send Service Request', choose the SR Type, describe the operations needed, decide on cluster scope (Yes/No), select the Asset/Cluster and Location, choose the receiver organization (IMO, MRO, or EXT. TECHNICIAN), and submit.",
        category: "Service Requests"
    },
    {
        question: "Why can't I see incoming service requests?",
        answer:
            "Only MRO organizations and technicians can see 'Incoming' service requests (operations to perform). Asset Owners see 'Outgoing' requests (operations they've requested) and 'Completed' requests. Check your user role and organization assignment.",
        category: "Service Requests"
    },
    {
        question: "How do I submit observations for a service request?",
        answer:
            "From an incoming service request, click 'Report Observation', select the Observation type (Okay/Not Okay), provide a detailed Report Summary of fault findings, set completion time, upload evidence, add service and spare parts requirements, and submit to the Asset Owner.",
        category: "Service Requests"
    },
    {
        question: "What should I include in an observation report?",
        answer:
            "Include the observation type, detailed findings of discovered faults, evidence photos/documents, time estimates for repairs, and itemized requirements for services and spare parts needed to fix the faults with quantities and costs.",
        category: "Service Requests"
    },
    {
        question: "How do MROs manage staff observations?",
        answer:
            "MROs can view fault findings submitted by staff, evaluate observation quality, review cost breakdowns, and use actions to 'Accept' (approve and forward), 'Reject' (return for revision), or 'Raise WO' (create work orders) for the observations.",
        category: "Service Requests"
    },

    // Work Order FAQs
    {
        question:
            "What's the difference between a service request and a work order?",
        answer:
            "Service requests are operations to be performed on assets, while work orders are formal assignments of maintenance or repair work. Work orders can be created from service requests, reported faults, or directly by Asset Owners.",
        category: "Work Orders"
    },
    {
        question: "How does the work order approval process work?",
        answer:
            "Work orders go through approval based on cost thresholds set in 'Work Order' > 'Configuration'. Higher amounts require higher-level approvals (Head O&M, GM, CEO). Once all required approvals are obtained, work orders are automatically sent to the assigned MRO organization.",
        category: "Work Orders"
    },
    {
        question: "How do I configure work order approvals?",
        answer:
            "Navigate to 'Work Order' > 'Configuration', click 'Create new level' to add approval categories with maximum amounts, assign specific users who can approve within those limits, and configure notification settings for work order updates.",
        category: "Work Orders"
    },
    {
        question: "Can MROs assign work orders to their staff?",
        answer:
            "Yes, after accepting an incoming work order, MROs can use 'Assign to staff' action, select the appropriate technician based on skills and availability, add assignment notes and instructions, and confirm the assignment.",
        category: "Work Orders"
    },
    {
        question: "What is work order certification and who can do it?",
        answer:
            "Work order certification is formal acknowledgment that service providers have met all requirements. Both Asset Owners and MROs can certify - Asset Owners certify from the client perspective, while MROs certify from the service provider perspective using the 'Work Order Certification' action.",
        category: "Work Orders"
    },
    {
        question: "How do I track work order progress?",
        answer:
            "Work order progress can be tracked through the observation details section showing service types, material providers, products, quantities, charges, and total costs. Both Asset Owners and MROs can view detailed progress throughout the lifecycle.",
        category: "Work Orders"
    },

    // Fault Reporting FAQs
    {
        question: "Who can report faults and how?",
        answer:
            "MRO organizations and technicians can report faults by navigating to 'Fault Portal', selecting the Cluster/Location/Asset, specifying Fault Type and detailed description, uploading supporting evidence, and submitting the report.",
        category: "Fault Reporting"
    },
    {
        question:
            "How do I access the fault portal for a specific Asset Owner?",
        answer:
            "Go to 'Fault Portal', select the Asset Owner from the dropdown, and the system generates a unique URL. Click 'Copy Link' to get direct access to report faults for that Asset Owner's assets.",
        category: "Fault Reporting"
    },
    {
        question:
            "What's the difference between Open Faults and Closed Faults?",
        answer:
            "Open Faults are pending review or action from Asset Owners. Closed Faults are resolved, rejected, or had work orders created from them. Use 'My Pending Requests' to track your submissions awaiting approval or review.",
        category: "Fault Reporting"
    },
    {
        question: "How do Asset Owners act on fault reports?",
        answer:
            "Asset Owners can review fault details, evaluate validity based on evidence, and use Actions dropdown to 'Reject' invalid faults, 'Raise SR' to create service requests, or 'Raise WO' to create work orders directly from fault reports.",
        category: "Fault Reporting"
    },

    // Pricebook Management FAQs
    {
        question: "What is a Pricebook and how do I create one?",
        answer:
            "A Pricebook is a centralized pricing catalog for services and materials. Create one by navigating to 'Pricebook', clicking 'Create Pricebook', entering a descriptive name and description, and clicking 'Create'.",
        category: "Pricebook Management"
    },
    {
        question: "How do I add items to a Pricebook?",
        answer:
            "Access the pricebook items list, click 'Actions' > 'Add Price', fill in item details including Name, Description, Brand, Model, and Price, then save. You can also use 'Bulk upload' with an Excel template for multiple items.",
        category: "Pricebook Management"
    },
    {
        question: "Can I bulk upload items to a Pricebook?",
        answer:
            "Yes, from the pricebook items list, click 'Actions' > 'Bulk upload', download the Excel template, fill it with Name, Description, Brand, Model, and Price columns, and upload the completed file.",
        category: "Pricebook Management"
    },
    {
        question: "How do I export Pricebook data?",
        answer:
            "From the pricebook items list, click 'Actions' > 'Download Material(s)' to export data as an Excel file for backup, sharing with team members, or importing to other systems.",
        category: "Pricebook Management"
    },
    {
        question: "How do I search for specific items in a Pricebook?",
        answer:
            "Use the search box to find items by name, description, brand, or model number. For example, search 'Crank Battery' for item names or 'GALES' for brand names to quickly locate specific materials.",
        category: "Pricebook Management"
    },

    // Warehouse Management FAQs
    {
        question: "How do I create a new warehouse location?",
        answer:
            "Navigate to 'Warehouse', click 'Add warehouse', fill in the Warehouse Name, Warehouse Address, select State and LGA from dropdowns, and click 'Create' to establish the new warehouse location.",
        category: "Warehouse Management"
    },
    {
        question:
            "What's the difference between Stock-In and Move Stock actions?",
        answer:
            "Stock-In (+) adds materials to inventory when receiving new stock or deliveries. Move Stock (-) removes materials from inventory when issuing them for work orders, specifying quantities, work order numbers, and receiving technicians.",
        category: "Warehouse Management"
    },
    {
        question: "How do I manage warehouse inventory?",
        answer:
            "Access warehouse inventory to view Material name, Description, Brand, Model, current Quantity, Price per unit, and last updated date. Use search functionality to find specific materials and monitor stock levels for reordering.",
        category: "Warehouse Management"
    },
    {
        question: "Can I bulk upload inventory items to a warehouse?",
        answer:
            "Yes, click 'Actions' > 'Bulk Upload', download the Excel template, fill it with Material, Description, Brand, Model, Quantity, and Price columns, and upload the completed file to import multiple items.",
        category: "Warehouse Management"
    },
    {
        question: "How do I create purchase orders from warehouse inventory?",
        answer:
            "From warehouse inventory, click 'Actions' > 'Purchase Order', identify items running low, select items for procurement, specify required quantities and preferred suppliers, and submit for approval and processing.",
        category: "Warehouse Management"
    },
    {
        question: "How do I export warehouse inventory data?",
        answer:
            "Click 'Actions' > 'Download (xls)' to export current inventory data as an Excel file containing all materials, quantities, prices, and stock movement history for backup, reporting, or analysis.",
        category: "Warehouse Management"
    },
    {
        question: "Can I track materials across multiple warehouse locations?",
        answer:
            "Yes, create multiple warehouses using 'Add warehouse' and track inventory separately for each location. Each warehouse maintains its own inventory levels, stock movements, and material tracking.",
        category: "Warehouse Management"
    },
    {
        question: "How do I link warehouse inventory to work orders?",
        answer:
            "When using 'Move Stock -' to issue materials, specify work order numbers to track which materials are being used for specific maintenance activities and projects, creating an audit trail.",
        category: "Warehouse Management"
    },
    {
        question: "What information is tracked for each inventory item?",
        answer:
            "Each inventory item tracks Material name, detailed Description, Brand, Model, current Quantity on hand, Price per unit, and last updated date for comprehensive inventory management and reporting.",
        category: "Warehouse Management"
    },
    {
        question: "How do I monitor stock levels and set reorder points?",
        answer:
            "Review the quantity column regularly, identify items running low, and establish minimum stock levels for critical materials. Use warehouse reporting to analyze usage patterns and optimize procurement planning.",
        category: "Warehouse Management"
    }
];
